// src/pages/admin/Payments.tsx
'use client';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import PaymentsTable from '../../components/admin/PaymentsTable';
import PaymentsTableToolbar from '../../components/admin/PaymentsTableToolbar';
import { listarPagamentos } from '../../services/pagamentoInscricaoService';
import type { PagamentoInscricao, PaymentsFilter } from '../../types/pagamento';
import dayjs from 'dayjs';
import { Button, CircularProgress, Menu, MenuItem } from '@mui/material';
import { downloadRelatorioInscricoesCsv } from '../../services/inscricaoTorneioService';

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const extractTournamentIdFromSearch = (search: string): string | null => {
    if (!search) return null;
    const params = new URLSearchParams(search);
    const raw = params.get('tournamentId');
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (!UUID_REGEX.test(trimmed)) {
        return null;
    }
    return trimmed;
};


const Payments: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const initialTournamentId = extractTournamentIdFromSearch(location.search);

    const [loading, setLoading] = useState<boolean>(false);
    const [pagamentos, setPagamentos] = useState<PagamentoInscricao[]>([]);
    const [filters, setFilters] = useState<PaymentsFilter>(() => {
        const initial: PaymentsFilter = {};
        if (initialTournamentId) {
            initial.torneioId = initialTournamentId;
        }
        return initial;
    });
    const [reportsAnchorEl, setReportsAnchorEl] = useState<null | HTMLElement>(null);
    const [reportsLoading, setReportsLoading] = useState<boolean>(false);
    const reportsOpen = Boolean(reportsAnchorEl);
    const hasAppliedInitialUrlFilters = useRef(false);
    const lastUrlTournamentIdRef = useRef<string | null>(initialTournamentId ?? null);
    const isUpdatingUrlRef = useRef(false);

    useEffect(() => {
        const urlTournamentId = extractTournamentIdFromSearch(location.search);

        if (!hasAppliedInitialUrlFilters.current) {
            hasAppliedInitialUrlFilters.current = true;
            lastUrlTournamentIdRef.current = urlTournamentId;
            if (urlTournamentId) {
                setFilters((prev) => (prev.torneioId ? prev : { ...prev, torneioId: urlTournamentId }));
            }
            return;
        }

        if (isUpdatingUrlRef.current) {
            isUpdatingUrlRef.current = false;
            lastUrlTournamentIdRef.current = urlTournamentId;
            return;
        }

        if (urlTournamentId !== lastUrlTournamentIdRef.current) {
            lastUrlTournamentIdRef.current = urlTournamentId;
            setFilters((prev) => {
                if (urlTournamentId) {
                    return { ...prev, torneioId: urlTournamentId };
                }
                if (!prev.torneioId) {
                    return prev;
                }
                return { ...prev, torneioId: null };
            });
        }
    }, [location.search]);

    useEffect(() => {
        if (!hasAppliedInitialUrlFilters.current) {
            return;
        }

        const currentTournamentId = filters.torneioId ?? null;
        if (currentTournamentId === lastUrlTournamentIdRef.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        if (currentTournamentId) {
            params.set('tournamentId', currentTournamentId);
        } else {
            params.delete('tournamentId');
        }

        lastUrlTournamentIdRef.current = currentTournamentId;
        isUpdatingUrlRef.current = true;

        const searchString = params.toString();
        const nextUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname;
        navigate(nextUrl, { replace: true });
    }, [filters.torneioId, location.pathname, location.search, navigate]);

    const openReportsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setReportsAnchorEl(event.currentTarget);
    };
    const closeReportsMenu = () => setReportsAnchorEl(null);

    const handleDownloadCsv = async () => {
        // Fecha o menu imediatamente ao iniciar o download
        closeReportsMenu();
        setReportsLoading(true);
        try {
            await downloadRelatorioInscricoesCsv({
                torneioId: filters.torneioId || undefined,
                categoriaTorneioId: filters.categoriaId || undefined,
                atletaId: filters.atletaId || undefined,
                dataCriacaoInicio: filters.dataInicio ? new Date(filters.dataInicio) : undefined,
                dataCriacaoFim: filters.dataFim ? new Date(filters.dataFim) : undefined,
                status: (filters.status as any) || undefined,
            });
        } finally {
            setReportsLoading(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listarPagamentos({
                page: 0,
                size: 50,
                status: (filters.status as any) || undefined,
                atletaId: filters.atletaId || undefined,
                categoriaId: filters.categoriaId || undefined,
                torneioId: filters.torneioId ?? null,
            });
            const items = (result.items as unknown as PagamentoInscricao[]) || [];
            let filtered = items;
            // Filtra por torneio (cliente)
            if (filters.torneioId) {
              filtered = filtered.filter((p) => (p.inscricoes || []).some((i) => i.categoriaTorneio?.torneio?.id === filters.torneioId));
            }
            // Filtra por datas (cliente)
            if (filters.dataInicio) {
              const start = dayjs(filters.dataInicio).startOf('day');
              filtered = filtered.filter((p) => {
                const d = dayjs(p.dataPagamento || p.criadoEm);
                return d.isValid() && (d.isAfter(start) || d.isSame(start));
              });
            }
            if (filters.dataFim) {
              const end = dayjs(filters.dataFim).endOf('day');
              filtered = filtered.filter((p) => {
                const d = dayjs(p.dataPagamento || p.criadoEm);
                return d.isValid() && (d.isBefore(end) || d.isSame(end));
              });
            }
            setPagamentos(filtered);
        } catch (err) {
            // erro silencioso: manter UX discreta
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Fragment>
            <div className="main-wrapper">
                <Header />
                <div className="main-content theme-dark-bg right-chat-active">

                    <div className="middle-sidebar-bottom">
                        <div className="middle-sidebar-left">
                            <div className="middle-wrap" style={{ maxWidth: '1200px' }}>
                                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                                    <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <Link to="/defaultsettings" className="d-inline-block mt-2"><i className="ti-arrow-left font-sm text-white"></i></Link>
                                            <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">Pagamentos</h4>
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                            {/* financeiro-dashboard-plataforma Phase 5 (Task 10, Should da PRD) — link para o hub
                                                financeiro da plataforma; esta tela continua existindo como listagem transacional
                                                (sem KPI, sem redirect — decisão já registrada na PRD). */}
                                            <Link to="/admin/financeiro" className="btn btn-sm bg-white text-dark fw-600">
                                                Ver financeiro
                                            </Link>
                                            <button onClick={load} disabled={loading} className="btn btn-sm bg-white text-dark fw-600">
                                                {loading ? 'Atualizando...' : 'Atualizar'}
                                            </button>
                                            <Button
                                                id="payments-reports-button"
                                                aria-controls={reportsOpen ? 'payments-reports-menu' : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={reportsOpen ? 'true' : undefined}
                                                variant="contained"
                                                color="secondary"
                                                size="small"
                                                onClick={openReportsMenu}
                                                disabled={reportsLoading}
                                                sx={{ bgcolor: '#fff', color: '#333', '&:hover': { bgcolor: '#f5f5f5' } }}
                                            >
                                                {reportsLoading ? (
                                                    <>
                                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                                        Relatórios
                                                    </>
                                                ) : (
                                                    'Relatórios'
                                                )}
                                            </Button>
                                            <Menu
                                                id="payments-reports-menu"
                                                anchorEl={reportsAnchorEl}
                                                open={reportsOpen}
                                                onClose={closeReportsMenu}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            >
                                                <MenuItem onClick={handleDownloadCsv} disabled={reportsLoading}>
                                                    Relatório de pagamentos (CSV)
                                                </MenuItem>
                                            </Menu>
                                        </div>
                                    </div>
                                </div>
                                <PaymentsTableToolbar
                                  availableFilters={['atleta','torneio','categoria','data','status']}
                                  initialFilters={{}}
                                  value={filters}
                                  onChange={setFilters}
                                  onApply={() => load()}
                                  onReset={() => { setFilters({}); }}
                                  showRefreshButton
                                  onRefresh={() => load()}
                                />
                                <PaymentsTable pagamentos={pagamentos} loading={loading} />
                            </div>
                        </div>

                    </div>
                </div>
            </div>

        </Fragment>
    )
}

export default Payments
